import { AJMAN_DISTRICTS } from '../../data/ajmanDistricts';

/**
 * Simple SVG map for GoSnel Maps page.
 * Shows Ajman districts as outlines and contacts as dots.
 * No Google Maps API needed.
 */
export function GoSnelMap({ contacts, selectedDistrict, onDistrictClick }) {
  // Compute overall bounds for viewBox
  const allLats = AJMAN_DISTRICTS.flatMap(d => [d.bounds.minLat, d.bounds.maxLat]);
  const allLngs = AJMAN_DISTRICTS.flatMap(d => [d.bounds.minLng, d.bounds.maxLng]);
  const minLng = Math.min(...allLngs) - 0.008;
  const maxLng = Math.max(...allLngs) + 0.008;
  const minLat = Math.min(...allLats) - 0.008;
  const maxLat = Math.max(...allLats) + 0.008;
  const lngRange = maxLng - minLng;
  const latRange = maxLat - minLat;

  // Count contacts per district for labeling
  const counts = {};
  (contacts || []).forEach(c => {
    const area = c.area || c.district || 'غير محدد';
    counts[area] = (counts[area] || 0) + 1;
  });

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg
        viewBox={`${minLng} ${-maxLat} ${lngRange} ${latRange}`}
        style={{ width: '100%', height: '100%' }}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Background */}
        <rect x={minLng} y={-maxLat} width={lngRange} height={latRange} fill="#f0f9ff" />

        {/* District rectangles */}
        {AJMAN_DISTRICTS.map((district) => {
          const isSelected = selectedDistrict?.name === district.name;
          const count = counts[district.nameAr] || counts[district.name] || 0;

          return (
            <g key={district.name}>
              <rect
                x={district.bounds.minLng}
                y={-district.bounds.maxLat}
                width={district.bounds.maxLng - district.bounds.minLng}
                height={district.bounds.maxLat - district.bounds.minLat}
                fill={isSelected ? 'rgba(78, 205, 196, 0.35)' : 'rgba(148, 163, 184, 0.08)'}
                stroke={isSelected ? '#4ecdc4' : '#94a3b8'}
                strokeWidth={isSelected ? '0.004' : '0.002'}
                rx="0.003"
                style={{ cursor: 'pointer' }}
                onClick={() => onDistrictClick && onDistrictClick(district)}
              />
              {/* District name label */}
              <text
                x={district.center.lng}
                y={-district.center.lat}
                textAnchor="middle"
                dominantBaseline="central"
                fill={isSelected ? '#0f766e' : '#475569'}
                fontSize="0.018"
                fontWeight={isSelected ? 'bold' : '600'}
                style={{ pointerEvents: 'none' }}
              >
                {district.nameAr}
              </text>
              {/* Contact count if any */}
              {count > 0 && (
                <text
                  x={district.center.lng}
                  y={-district.center.lat + 0.025}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#10b981"
                  fontSize="0.015"
                  fontWeight="bold"
                  style={{ pointerEvents: 'none' }}
                >
                  {count}
                </text>
              )}
            </g>
          );
        })}

        {/* Contact dots */}
        {(contacts || []).map((contact, idx) => {
          // Use lat/lng if available, otherwise approximate from area
          let lat = contact.latitude || contact.lat;
          let lng = contact.longitude || contact.lng;

          // If no coordinates, place roughly in center of matching district
          if (!lat || !lng) {
            const district = AJMAN_DISTRICTS.find(
              d => d.nameAr === contact.area || d.name === contact.area ||
                   d.nameAr === contact.district || d.name === contact.district
            );
            if (district) {
              lat = district.center.lat;
              lng = district.center.lng;
            } else {
              return null; // Skip contacts without location
            }
          }

          return (
            <g key={contact.id || idx}>
              <circle
                cx={lng}
                cy={-lat}
                r="0.004"
                fill="#ef4444"
                stroke="#ffffff"
                strokeWidth="0.001"
                style={{ cursor: 'pointer' }}
              >
                <title>{contact.name || 'مطعم'} - {contact.area || contact.district || ''}</title>
              </circle>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div style={{
        position: 'absolute',
        bottom: 8,
        right: 8,
        background: 'rgba(255,255,255,0.95)',
        padding: '8px 12px',
        borderRadius: 8,
        fontSize: 11,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>📍 دليل الخريطة</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', border: '1px solid #fff' }} />
          <span>جهات الاتصال</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 10, height: 10, background: 'rgba(78,205,196,0.35)', border: '1px solid #4ecdc4' }} />
          <span>المنطقة المختارة</span>
        </div>
      </div>
    </div>
  );
}
